import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { UserTagBadge } from '@/components/user/UserTagBadge';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import { Heart, MessageSquare, Paperclip, X, EyeOff, Send, Trash2, CircleDollarSign } from 'lucide-react';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

import { LevelBadge } from '@/components/ranking/LevelBadge';
import { XpGainToast } from '@/components/ranking/XpGainToast';
import { useAuth, type User } from '@/context/AuthContext';
import { api, unwrapApiResponse } from '@/utils/api';
import { imageUrl, uploadImage, generateWorkLink, generateChapterLink } from '@/utils/utils';
import {
    getUserImageUrl,
    getMolduraImageUrl,
    getObraImageUrl,
    DEFAULT_AVATAR,
    avatarClipClassFromMolduraTipo,
} from '@/utils/image-utils';
import { MangaReferenceCard } from '@/components/MangaReferenceCard';
import FigurinhaPicker from '@/components/capitulo/FigurinhaPicker';
import { buildFigurinhaMap, renderCommentWithFigurinhas, type FigurinhaItem } from '@/utils/figurinhas';

interface Comment {
    id: number;
    comentario: string;
    imagem?: string;
    criado_em: string;
    curtido: boolean;
    total_curtidas: number;
    usuario: {
        id: number;
        nome: string;
        nick?: string;
        imagem?: string;
        moldura?: string;
        /** 1 = avatar circular, 2 = avatar quadrado (`mol_tipo` da moldura) */
        mol_tipo?: number;
        nivel?: number;
        tags?: Array<{
            tag_id: number;
            tag_nome: string;
            tag_cor_texto: string;
            tag_cor_fundo: string;
            tag_imagem?: string | null;
        }>;
    };
    filhos?: Comment[];
    superchat?: {
        ativo?: boolean;
        amount?: number;
        amount_centavos?: number;
        moeda?: string;
        status?: string;
    } | null;
    contexto?: {
        tipo?: 'comunidade' | 'obra' | 'capitulo' | 'lista';
        obra_id?: number | null;
        obra_nome?: string | null;
        obra_imagem?: string | null;
        capitulo_numero?: number | null;
        capitulo_id?: number | null;
        capitulo_nome?: string | null;
        lista_id?: number;
        lista_nome?: string | null;
    };
}

/** IDs válidos obrigatórios — evita `undefined === undefined` ao comparar autor vs sessão. */
function resolveSessionUserId(u: User | null | undefined): number | null {
    if (!u) return null;
    const raw = u.usr_id ?? (u as { id?: unknown }).id;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function resolveCommentAuthorId(usuario: Comment['usuario']): number | null {
    const raw = usuario?.id ?? (usuario as { usr_id?: unknown }).usr_id;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** Só exibe o card de referência se houver metadado útil além de `capitulo_id`. */
function capituloContextoTemDadosAlemDoId(ctx: NonNullable<Comment['contexto']>): boolean {
    if (ctx.tipo !== 'capitulo' || !ctx.capitulo_id) return false;
    if (ctx.obra_id != null && ctx.obra_id > 0) return true;
    if (ctx.obra_nome?.trim()) return true;
    if (ctx.obra_imagem?.trim()) return true;
    if (ctx.capitulo_numero != null) return true;
    if (ctx.capitulo_nome?.trim()) return true;
    return false;
}

/** Frases de agradecimento ao capítulo (med-app `ObraComments`); o texto final inclui `:corazao2:` como no app. */
const THANKS_TEMPLATES: readonly string[] = [
    'Obrigado pelo capitulo!',
    'Muito obrigado pelo capitulo!',
    'Valeu pelo capitulo!',
    'Agradeco pelo capitulo!',
    'Obrigado por este capitulo!',
    'Obrigado por esse capitulo!',
    'Obrigado por mais um capitulo!',
    'Valeu por mais um capitulo!',
    'Agradeco por mais um capitulo!',
    'Obrigado pelo capitulo de hoje!',
    'Valeu pelo capitulo de hoje!',
    'Agradeco pelo capitulo de hoje!',
    'Obrigado pelo novo capitulo!',
    'Valeu pelo novo capitulo!',
    'Agradeco pelo novo capitulo!',
    'Obrigado pelo proximo capitulo!',
    'Valeu pelo proximo capitulo!',
    'Agradeco pelo proximo capitulo!',
    'Obrigado por disponibilizar o capitulo!',
    'Valeu por disponibilizar o capitulo!',
    'Agradeco por disponibilizar o capitulo!',
    'Obrigado por postar o capitulo!',
    'Valeu por postar o capitulo!',
    'Agradeco por postar o capitulo!',
    'Obrigado por publicar o capitulo!',
    'Valeu por publicar o capitulo!',
    'Agradeco por publicar o capitulo!',
    'Obrigado pelo capitulo, valeu!',
    'Valeu pelo capitulo, obrigado!',
    'Agradeco pelo capitulo, obrigado!',
    'Obrigado por trazer o capitulo!',
    'Valeu por trazer o capitulo!',
    'Agradeco por trazer o capitulo!',
    'Obrigado por enviar o capitulo!',
    'Valeu por enviar o capitulo!',
    'Agradeco por enviar o capitulo!',
    'Obrigado por liberar o capitulo!',
    'Valeu por liberar o capitulo!',
    'Agradeco por liberar o capitulo!',
    'Obrigado novamente pelo capitulo!',
];

/** Agradecimentos na página da obra (só `obraId`, sem capítulo). */
const THANKS_OBRA_TEMPLATES: readonly string[] = [
    'Obrigado pela obra!',
    'Muito obrigado pela obra!',
    'Valeu pela obra!',
    'Agradeco pela obra!',
    'Obrigado pelo scan!',
    'Valeu pelo scan!',
    'Agradeco pelo scan!',
    'Obrigado pelo trabalho!',
    'Valeu pelo trabalho!',
    'Agradeco pelo trabalho!',
    'Obrigado pela traducao!',
    'Valeu pela traducao!',
    'Agradeco pela traducao!',
    'Obrigado por manter a obra!',
    'Valeu por manter a obra!',
    'Obrigado pela dedicacao!',
    'Valeu pela dedicacao!',
    'Agradeco pela dedicacao!',
    'Obrigado pelo esforco!',
    'Valeu pelo esforco!',
    'Obrigado pelos capitulos!',
    'Valeu pelos capitulos!',
    'Agradeco pelos capitulos!',
    'Obrigado por disponibilizar a obra!',
    'Valeu por disponibilizar a obra!',
    'Agradeco por disponibilizar a obra!',
    'Obrigado por postar!',
    'Valeu por postar!',
    'Obrigado novamente pela obra!',
];

const SUPER_CHAT_LEVELS = ['5', '10', '20', '50', '100'] as const;

function superChatToneByAmount(raw: string): string {
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) return 'ring-white/10 bg-white/[0.07]';
    if (amount < 10) return 'ring-emerald-500/45 bg-emerald-500/10';
    if (amount < 20) return 'ring-cyan-500/45 bg-cyan-500/10';
    if (amount < 50) return 'ring-violet-500/45 bg-violet-500/10';
    if (amount < 100) return 'ring-amber-500/45 bg-amber-500/10';
    return 'ring-rose-500/45 bg-rose-500/10';
}

function normalizeCommentNode(raw: any): Comment {
    const r = raw?.reacoes;
    const curtido = r?.minha_reacao === 'like';
    const total_curtidas = typeof r?.like === 'number' ? r.like : 0;
    return {
        ...raw,
        curtido,
        total_curtidas,
        filhos: Array.isArray(raw?.filhos) ? raw.filhos.map(normalizeCommentNode) : undefined,
    };
}

/** Detecta [SPOILER] ou <spoiler> no texto (início ou fim). Convenção só no front, sem backend. */
function parseSpoiler(text: string): { isSpoiler: boolean; displayText: string } {
    if (!text || typeof text !== 'string') return { isSpoiler: false, displayText: text || '' };
    const t = text.trim();
    const startMatch = t.match(/^(\[SPOILER\]|<\s*spoiler\s*>)\s*/i);
    const endMatch = t.match(/\s*(\[\/?SPOILER\]|<\s*\/\s*spoiler\s*>)$/i);
    let displayText = t;
    if (startMatch) displayText = displayText.slice(startMatch[0].length).trim();
    if (endMatch) displayText = displayText.slice(0, -endMatch[0].length).trim();
    const isSpoiler = !!startMatch || !!endMatch;
    return { isSpoiler, displayText: displayText || '(spoiler)' };
}

interface ChapterCommentsProps {
    chapterId?: number;
    obraId?: number;
    initialComments?: any[];
    highlightCommentId?: number;
    onCountUpdate?: (count: number) => void;
    comunidade?: boolean;
    /** Feed de comentários de um utilizador (perfil público): mesmo formato que a comunidade, sem formulário novo. */
    perfilUsuarioId?: number;
    onPaginationMeta?: (meta: { totalItems: number; totalPages: number; page: number }) => void;
    pagina?: number;
    totalPages?: number;
}

export default function ChapterComments({
    chapterId,
    obraId,
    initialComments,
    highlightCommentId,
    onCountUpdate,
    comunidade = false,
    perfilUsuarioId,
    onPaginationMeta,
    pagina = 1,
    totalPages: _totalPages = 1,
}: ChapterCommentsProps) {
    const { isAuthenticated, user } = useAuth();
    const [highlightedId, setHighlightedId] = useState<number | null>(highlightCommentId || null);
    const [sortOrder, setSortOrder] = useState<'recentes' | 'curtidos' | 'antigos'>('recentes');
    // Inicializar com comentários do servidor se disponíveis
    const [comments, setComments] = useState<Comment[]>(initialComments || []);
    const [loading, setLoading] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showImageUpload, setShowImageUpload] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [markAsSpoiler, setMarkAsSpoiler] = useState(false);
    const [superChatOpen, setSuperChatOpen] = useState(false);
    const [superChatAmount, setSuperChatAmount] = useState('');
    const [customSuperChatOpen, setCustomSuperChatOpen] = useState(false);
    const [customSuperChatValue, setCustomSuperChatValue] = useState('');
    const imageInputRef = useRef<HTMLInputElement>(null);
    const newCommentTextareaRef = useRef<HTMLTextAreaElement>(null);
    const lastThanksIndexRef = useRef<number>(-1);
    const [figurinhaItems, setFigurinhaItems] = useState<FigurinhaItem[]>([]);
    const [figurinhaMap, setFigurinhaMap] = useState<Map<string, FigurinhaItem>>(() => new Map());

    // XP Toast State
    const [xpToast, setXpToast] = useState<{ show: boolean, xp: number, message: string }>({ show: false, xp: 0, message: '' });

    const handleXpGain = (xp: number, message: string) => {
        setXpToast({ show: true, xp, message });
    };

    const feedComContexto = Boolean(comunidade || perfilUsuarioId != null);

    const sortedComments = useMemo(() => {
        const copy = [...comments];
        if (sortOrder === 'curtidos') return copy.sort((a, b) => b.total_curtidas - a.total_curtidas);
        if (sortOrder === 'antigos') return copy.sort((a, b) => new Date(a.criado_em).getTime() - new Date(b.criado_em).getTime());
        return copy.sort((a, b) => new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime());
    }, [comments, sortOrder]);
    const canUseSuperchat = Boolean((chapterId || obraId) && !comunidade && perfilUsuarioId == null);
    const superChatIndex = Math.max(
        0,
        SUPER_CHAT_LEVELS.indexOf((superChatAmount || '20') as (typeof SUPER_CHAT_LEVELS)[number]),
    );
    const superChatFillPct = (superChatIndex / (SUPER_CHAT_LEVELS.length - 1)) * 100;
    const composerSuperChatRing =
        canUseSuperchat && superChatOpen ? superChatToneByAmount(superChatAmount || '20') : 'ring-transparent';

    const fillThanksTemplate = () => {
        const templates = chapterId ? THANKS_TEMPLATES : obraId ? THANKS_OBRA_TEMPLATES : null;
        if (!templates?.length) return;
        let nextIndex = Math.floor(Math.random() * templates.length);
        if (templates.length > 1 && nextIndex === lastThanksIndexRef.current) {
            nextIndex = (nextIndex + 1) % templates.length;
        }
        lastThanksIndexRef.current = nextIndex;
        const fallback = chapterId ? 'Obrigado pelo capitulo!' : 'Obrigado pela obra!';
        const base = templates[nextIndex] ?? fallback;
        const template = `${base} :corazao2:`;
        setNewComment(template);
        requestAnimationFrame(() => {
            const ta = newCommentTextareaRef.current;
            if (!ta) return;
            ta.focus();
            const end = template.length;
            ta.setSelectionRange(end, end);
        });
    };

    const insertFigurinhaInComposer = (code: string) => {
        const token = `:${code}:`;
        const ta = newCommentTextareaRef.current;
        const prev = newComment;
        if (!ta) {
            setNewComment(prev.trim() ? `${prev.trim()} ${token}` : token);
            return;
        }
        const start = ta.selectionStart ?? prev.length;
        const end = ta.selectionEnd ?? prev.length;
        const before = prev.slice(0, start);
        const after = prev.slice(end);
        const needsSpace = before.length > 0 && !/\s$/.test(before);
        const spacer = needsSpace ? ' ' : '';
        const gap = after.length > 0 && !after.startsWith(' ') ? ' ' : '';
        const next = before + spacer + token + gap + after;
        const caret = (before + spacer + token + gap).length;
        setNewComment(next);
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(caret, caret);
        });
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const raw = unwrapApiResponse<{ items?: FigurinhaItem[] }>(
                    await api.get('figurinhas', { requiresAuth: false }),
                );
                if (cancelled) return;
                const items = Array.isArray(raw?.items) ? raw.items : [];
                setFigurinhaItems(items);
                setFigurinhaMap(buildFigurinhaMap(items));
            } catch {
                if (!cancelled) {
                    setFigurinhaItems([]);
                    setFigurinhaMap(new Map());
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Buscar comentários
    const fetchComments = async () => {
        if (!chapterId && !obraId && !comunidade && perfilUsuarioId == null) return;

        setLoading(true);
        try {
            let url = '';
            let requiresAuth = false;
            if (perfilUsuarioId != null) {
                url = `comentarios/perfil/${perfilUsuarioId}?page=${pagina}&limit=30`;
                requiresAuth = true;
            } else if (comunidade) {
                url = `comentarios/comunidade?page=${pagina}&limit=30`;
            } else if (chapterId) {
                url = `comentarios?capitulo=${chapterId}&sem_paginacao=true`;
            } else if (obraId) {
                url = `comentarios?obra=${obraId}&sem_paginacao=true`;
            }

            const data = unwrapApiResponse<any>(await api.get(url, { requiresAuth }));

            let commentsList: Comment[] = [];
            if (Array.isArray(data)) {
                commentsList = data.map(normalizeCommentNode);
            } else if (data?.data && Array.isArray(data.data)) {
                commentsList = data.data.map(normalizeCommentNode);
            } else if (data?.comentarios && Array.isArray(data.comentarios)) {
                commentsList = data.comentarios.map(normalizeCommentNode);
            }

            if (onPaginationMeta && data?.pagination) {
                onPaginationMeta({
                    totalItems: Number(data.pagination.totalItems ?? 0),
                    totalPages: Math.max(1, Number(data.pagination.totalPages ?? 1)),
                    page: Number(data.pagination.page ?? pagina),
                });
            }

            setComments(commentsList);
            if (onCountUpdate) {
                onCountUpdate(countTotalComments(commentsList));
            }
        } catch (error) {
            console.error('Erro ao buscar comentários:', error);
            setComments([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Se já temos comentários iniciais do servidor, não buscar novamente
        if (initialComments && initialComments.length >= 0 && obraId) {
            return;
        }

        if (chapterId || obraId || comunidade || perfilUsuarioId != null) {
            fetchComments();
        }
    }, [chapterId, obraId, comunidade, perfilUsuarioId, pagina]);

    // Scroll até o comentário destacado
    useEffect(() => {
        if (highlightCommentId && comments.length > 0) {
            // Delay para garantir que o DOM foi renderizado
            setTimeout(() => {
                const commentElement = document.getElementById(`comment-${highlightCommentId}`);
                if (commentElement) {
                    commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setHighlightedId(highlightCommentId);

                    // Remover destaque após alguns segundos
                    setTimeout(() => {
                        setHighlightedId(null);
                    }, 5000);
                }
            }, 500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightCommentId, comments.length]);

    // Contar total de comentários (incluindo respostas)
    const countTotalComments = (comments: Comment[]): number => {
        let count = comments.length;
        comments.forEach(comment => {
            if (comment.filhos && comment.filhos.length > 0) {
                count += countTotalComments(comment.filhos);
            }
        });
        return count;
    };

    // Processa arquivo de imagem (input e drag-and-drop)
    const processImageFile = (file: File) => {
        if (!file.type.startsWith('image/')) {
            alert('Selecione apenas arquivos de imagem.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('A imagem deve ter no máximo 5MB');
            return;
        }
        setImageFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            if (e.target?.result) {
                setImagePreview(e.target.result as string);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processImageFile(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processImageFile(file);
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
    };

    // Submit comment
    const handleSubmitComment = async () => {
        if (!isAuthenticated) {
            alert('Você precisa estar logado para comentar');
            return;
        }

        if (!newComment.trim() && !imageFile) {
            return;
        }

        setSubmitting(true);
        try {
            let imageUrl: string | null = null;

            // Se houver imagem, fazer upload primeiro
            if (imageFile) {
                try {
                    imageUrl = await uploadImage(imageFile, 'comentarios');
                } catch (uploadError) {
                    console.error('Erro ao fazer upload da imagem:', uploadError);
                    alert('Erro ao fazer upload da imagem. Tente novamente.');
                    setSubmitting(false);
                    return;
                }
            }

            const commentText = newComment.trim() || '';
            const superChatAmountNum = Number(String(superChatAmount || '').replace(',', '.'));
            const hasSuperChat =
                canUseSuperchat &&
                superChatOpen &&
                Number.isFinite(superChatAmountNum) &&
                superChatAmountNum > 0;

            if (hasSuperChat) {
                const scPayload: Record<string, string | number | boolean> = {
                    comentario: commentText,
                    spoiler: markAsSpoiler,
                    amount: superChatAmountNum,
                    source: 'web',
                };
                if (chapterId) scPayload.capitulo = chapterId.toString();
                else if (obraId) scPayload.obra = obraId.toString();
                if (imageUrl) scPayload.imagem = imageUrl;

                const initData = unwrapApiResponse<{ checkoutUrl?: string }>(
                    await api.post('comentarios/superchat/iniciar', scPayload, { requiresAuth: true }),
                );
                if (!initData?.checkoutUrl) {
                    throw new Error('Checkout indisponível');
                }
                window.location.href = initData.checkoutUrl;
                return;
            }

            const payload: any = {
                comentario: markAsSpoiler ? `[SPOILER] ${commentText}` : commentText
            };

            if (chapterId) {
                payload.capitulo = chapterId.toString();
            } else if (obraId) {
                payload.obra = obraId.toString();
            }

            if (imageUrl) {
                payload.imagem = imageUrl;
            }

            const response: any = unwrapApiResponse(await api.post('comentarios', payload, { requiresAuth: true }));

            // XP Gain
            if (response.xp > 0) {
                handleXpGain(response.xp, 'Comentário');
            }

            setNewComment('');
            setMarkAsSpoiler(false);
            setSuperChatOpen(false);
            setCustomSuperChatOpen(false);
            removeImage();
            setShowImageUpload(false);
            await fetchComments();
        } catch (error) {
            console.error('Erro ao enviar comentário:', error);
            alert('Erro ao enviar comentário. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    };

    const SORT_LABELS = {
        recentes: 'Mais Recentes',
        curtidos: 'Mais Curtidos',
        antigos: 'Mais Antigos',
    } as const;

    return (
        <div className="w-full max-w-4xl mx-auto px-3 md:px-6 py-6 md:py-12 border-t border-white/10 mt-8 md:mt-12 overflow-hidden">
            {/* Cabeçalho + filtros (somente obra/capítulo, não no feed de comunidade/perfil) */}
            {!feedComContexto && (
                <div className="mb-6 md:mb-8">
                    <div className="flex items-baseline gap-2.5 mb-4">
                        <h2 className="text-xl md:text-2xl font-black uppercase italic tracking-tighter text-white">
                            Comentários
                        </h2>
                        <span className="text-sm font-medium text-white/40">({countTotalComments(comments)})</span>
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/35 mb-2.5">
                        Ordenar por
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                        {(['recentes', 'curtidos', 'antigos'] as const).map((order) => {
                            const isActive = sortOrder === order;
                            return (
                                <button
                                    key={order}
                                    type="button"
                                    onClick={() => setSortOrder(order)}
                                    className={`px-4 py-2 rounded-full text-[11px] font-black uppercase tracking-wider transition-all duration-200 ${
                                        isActive
                                            ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                                            : 'bg-white/[0.06] text-white/50 hover:bg-white/[0.10] hover:text-white/80'
                                    }`}
                                >
                                    {SORT_LABELS[order]}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Formulário de comentário */}
            {isAuthenticated && perfilUsuarioId == null && (
                <div
                    className={`mb-6 md:mb-8 rounded-xl border-2 border-dashed transition-colors ${isDragging ? 'border-orange-500/60 bg-orange-500/5' : 'border-white/10'}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className="p-3 md:p-4">
                        <textarea
                            ref={newCommentTextareaRef}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Escreva seu comentário... (opcional se anexar imagem). Você também pode arrastar uma imagem aqui."
                            className={`w-full bg-white/5 border border-white/10 rounded-xl p-3 md:p-4 text-white placeholder-white/40 focus:outline-none focus:border-orange-500/50 resize-none min-h-[80px] md:min-h-[100px] mb-3 text-sm ring-1 ${composerSuperChatRing}`}
                            rows={4}
                            maxLength={1000}
                        />

                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <input
                                ref={imageInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageSelect}
                            />
                            {(chapterId || obraId) && !comunidade ? (
                                <button
                                    type="button"
                                    onClick={fillThanksTemplate}
                                    disabled={submitting}
                                    className="px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-400/35 text-emerald-200 hover:bg-emerald-500/25 transition-colors text-xs font-medium disabled:opacity-50"
                                    title="Preencher comentário de agradecimento"
                                >
                                    Agradecer
                                </button>
                            ) : null}
                            <FigurinhaPicker
                                items={figurinhaItems}
                                onPick={insertFigurinhaInComposer}
                                disabled={submitting}
                                placement="above"
                            />
                            <button
                                type="button"
                                onClick={() => imageInputRef.current?.click()}
                                className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:text-white hover:border-orange-500/50 transition-colors"
                                title="Anexar imagem"
                            >
                                <Paperclip size={18} />
                            </button>
                            {imagePreview && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <img
                                        src={imagePreview || ''}
                                        alt="Preview"
                                        className="h-14 w-14 object-cover rounded-lg border border-white/10"
                                    />
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        className="p-1.5 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                        title="Remover imagem"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                            {canUseSuperchat && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSuperChatOpen((v) => !v);
                                        if (!superChatAmount) setSuperChatAmount('20');
                                    }}
                                    className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-lg border transition-colors text-xs md:text-sm font-medium ${
                                        superChatOpen
                                            ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/30'
                                            : 'bg-white/5 border-white/10 text-white/70 hover:text-white hover:border-emerald-500/40'
                                    }`}
                                    title="Destacar comentário com valor (pagamento via LivePix)"
                                >
                                    <CircleDollarSign size={16} className="shrink-0" />
                                    Super chat
                                </button>
                            )}
                        </div>

                        {canUseSuperchat && superChatOpen && (
                            <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.03] p-2.5 md:p-3">
                                <div className="mb-1 text-center text-base md:text-lg font-semibold tracking-wide text-white">
                                    R$ {Number(superChatAmount || '20').toFixed(2)} BRL
                                </div>
                                <div className="relative px-1">
                                    <div className="absolute left-1 right-1 top-2.5 h-0.5 rounded-full bg-white/20" />
                                    <div
                                        className="absolute left-1 top-2.5 h-0.5 rounded-full bg-cyan-400"
                                        style={{ width: `calc(${superChatFillPct}% - 2px)` }}
                                    />
                                    <div className="relative flex items-center justify-between pt-0.5">
                                        {SUPER_CHAT_LEVELS.map((v, idx) => (
                                            <span
                                                key={v}
                                                className={`h-1 w-1 rounded-full ${idx <= superChatIndex ? 'bg-cyan-300' : 'bg-white/45'}`}
                                            />
                                        ))}
                                    </div>
                                    <input
                                        type="range"
                                        min={0}
                                        max={SUPER_CHAT_LEVELS.length - 1}
                                        step={1}
                                        value={superChatIndex}
                                        onChange={(e) => {
                                            const idx = Number(e.target.value);
                                            setSuperChatAmount(SUPER_CHAT_LEVELS[idx] ?? '20');
                                        }}
                                        className="mt-0.5 w-full accent-cyan-400 bg-transparent"
                                    />
                                </div>
                                <div className="mt-1.5 flex flex-wrap items-center justify-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setCustomSuperChatOpen((v) => !v)}
                                        className="px-2 py-1 rounded-md bg-white/10 border border-white/15 text-[11px] text-white/80 hover:bg-white/15 transition-colors"
                                    >
                                        Personalizar
                                    </button>
                                    {customSuperChatOpen ? (
                                        <input
                                            type="number"
                                            min={1}
                                            step="0.01"
                                            inputMode="decimal"
                                            placeholder="15,90"
                                            value={customSuperChatValue}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(',', '.');
                                                setCustomSuperChatValue(e.target.value);
                                                const n = Number(raw);
                                                if (Number.isFinite(n) && n > 0) {
                                                    setSuperChatAmount(String(n));
                                                }
                                            }}
                                            className="w-24 bg-black/25 border border-white/10 rounded-md px-2 py-1 text-[11px] text-white focus:outline-none focus:border-emerald-400/60"
                                        />
                                    ) : null}
                                </div>
                            </div>
                        )}

                        <label className="flex items-center gap-2 text-white/60 hover:text-white/80 cursor-pointer text-xs md:text-sm">
                            <input
                                type="checkbox"
                                checked={markAsSpoiler}
                                onChange={(e) => setMarkAsSpoiler(e.target.checked)}
                                className="rounded border-white/30 bg-white/5 text-orange-500 focus:ring-orange-500/50"
                            />
                            <EyeOff size={14} />
                            Marcar como spoiler
                        </label>
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-white/40 text-xs whitespace-nowrap">{newComment.length}/1000</span>
                            <button
                                onClick={handleSubmitComment}
                                disabled={(!newComment.trim() && !imageFile) || submitting}
                                className="px-4 md:px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm whitespace-nowrap"
                            >
                                {submitting ? (
                                    <>
                                        <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span className="hidden md:inline">Enviando...</span>
                                    </>
                                ) : (
                                    <>
                                        <Send size={14} className="md:w-4 md:h-4" />
                                        <span>Comentar</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lista de comentários */}
            {loading ? (
                <div className="py-20 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                    <p className="mt-4 text-white/40 text-sm">Carregando comentários...</p>
                </div>
            ) : comments.length === 0 ? (
                <div className="py-20 text-center">
                    <MessageSquare size={48} className="mx-auto text-white/10 mb-4" />
                    <p className="text-white/40">
                        {comunidade
                            ? 'Ainda não há comentários na comunidade.'
                            : perfilUsuarioId != null
                              ? 'Este utilizador ainda não comentou.'
                              : chapterId
                                ? 'Ainda não há comentários para este capítulo.'
                                : 'Ainda não há comentários para esta obra.'}
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-6 md:gap-8">
                    {sortedComments.map((comment) => (
                        <CommentItem
                            key={comment.id}
                            comment={comment}
                            chapterId={chapterId}
                            obraId={obraId}
                            onRefresh={fetchComments}
                            onImageClick={setSelectedImage}
                            onXpGain={handleXpGain}
                            highlightedId={highlightedId}
                            comunidade={comunidade}
                            feedContexto={feedComContexto}
                            feedSomenteLeitura={perfilUsuarioId != null}
                            figurinhaMap={figurinhaMap}
                            figurinhaItems={figurinhaItems}
                        />
                    ))}
                </div>
            )}

            {/* Modal de imagem */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        onClick={() => setSelectedImage(null)}
                        className="absolute top-4 right-4 p-2 bg-black/80 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <X size={24} className="text-white" />
                    </button>
                    <img
                        src={selectedImage || ''}
                        alt="Imagem do comentário"
                        className="max-w-full max-h-[90vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            <XpGainToast
                isVisible={xpToast.show}
                xp={xpToast.xp}
                message={xpToast.message}
                onClose={() => setXpToast(prev => ({ ...prev, show: false }))}
            />
        </div>
    );
}

/** Envolve conteúdo (texto + img) em blur; clique revela. */
function SpoilerOverlay({ children, className = '' }: { children: React.ReactNode; className?: string }) {
    const [revealed, setRevealed] = useState(false);
    if (revealed) return <div className={className}>{children}</div>;
    return (
        <div className={`relative ${className}`}>
            <div className="select-none pointer-events-none blur-md saturate-0 contrast-50 scale-[1.02] min-h-[2rem]" aria-hidden>
                {children}
            </div>
            <button
                type="button"
                onClick={() => setRevealed(true)}
                className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 hover:bg-black/70 transition-colors rounded-lg text-white/90 text-sm cursor-pointer"
            >
                <EyeOff size={18} />
                Spoiler — clique para revelar
            </button>
        </div>
    );
}

/** Evita indentação infinita: após este nível a margem não aumenta (só a borda indica aninhamento). */
const MAX_COMMENT_THREAD_INDENT_DEPTH = 2;

/**
 * Pais com `depth` até este valor ainda listam `filhos` no cartão.
 * Com 0: só a raiz (`depth === 0`) mostra respostas embaixo; a partir da 1.ª resposta (`depth >= 1`)
 * os filhos vão para "Ver respostas" + modal (evita o 3.º cartão espremido na página).
 */
const MAX_PARENT_DEPTH_FOR_INLINE_REPLIES = 0;

function countCommentsInSubtree(nodes: Comment[] | undefined): number {
    if (!nodes?.length) return 0;
    return nodes.reduce((acc, c) => acc + 1 + countCommentsInSubtree(c.filhos), 0);
}

interface CommentItemProps {
    comment: Comment;
    chapterId?: number;
    obraId?: number;
    onRefresh: () => void;
    onImageClick: (url: string) => void;
    isReply?: boolean;
    depth?: number;
    onXpGain?: (xp: number, message: string) => void;
    highlightedId?: number | null;
    comunidade?: boolean;
    /** Cartão de contexto (obra / capítulo / comunidade / lista), como no feed da comunidade. */
    feedContexto?: boolean;
    /** Perfil alheio: sem responder aqui (contexto variável). */
    feedSomenteLeitura?: boolean;
    figurinhaMap: Map<string, FigurinhaItem>;
    figurinhaItems: FigurinhaItem[];
    /** Dentro do modal de thread profunda: expande toda a subárvore inline. */
    forceInlineSubthread?: boolean;
}

function DeepRepliesThreadModal({
    replies,
    parentDepth,
    onClose,
    chapterId,
    obraId,
    onRefresh,
    onImageClick,
    onXpGain,
    highlightedId,
    comunidade,
    feedContexto,
    feedSomenteLeitura,
    figurinhaMap,
    figurinhaItems,
}: {
    replies: Comment[];
    parentDepth: number;
    onClose: () => void;
    chapterId?: number;
    obraId?: number;
    onRefresh: () => void;
    onImageClick: (url: string) => void;
    onXpGain?: (xp: number, message: string) => void;
    highlightedId?: number | null;
    comunidade?: boolean;
    feedContexto?: boolean;
    feedSomenteLeitura?: boolean;
    figurinhaMap: Map<string, FigurinhaItem>;
    figurinhaItems: FigurinhaItem[];
}) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const content = (
        <div className="fixed inset-0 z-[290] flex items-end justify-center sm:items-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="deep-replies-modal-title">
            <button
                type="button"
                className="absolute inset-0 bg-black/75 backdrop-blur-sm"
                onClick={onClose}
                aria-label="Fechar"
            />
            <div className="relative z-10 flex w-full max-w-lg sm:max-w-xl flex-col rounded-t-2xl sm:rounded-2xl border border-white/10 bg-neutral-950 shadow-2xl max-h-[min(90dvh,720px)] sm:max-h-[85vh]">
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
                    <h2 id="deep-replies-modal-title" className="text-sm font-semibold text-white">
                        Respostas
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-2 text-white/60 hover:bg-white/10 hover:text-white transition-colors"
                        aria-label="Fechar"
                    >
                        <X size={20} />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 sm:px-4">
                    <div className="flex flex-col gap-5 md:gap-6">
                        {replies.map((reply) => (
                            <CommentItem
                                key={reply.id}
                                comment={reply}
                                chapterId={chapterId}
                                obraId={obraId}
                                onRefresh={onRefresh}
                                onImageClick={onImageClick}
                                isReply
                                depth={parentDepth + 1}
                                onXpGain={onXpGain}
                                highlightedId={highlightedId}
                                comunidade={comunidade}
                                feedContexto={feedContexto}
                                feedSomenteLeitura={feedSomenteLeitura}
                                figurinhaMap={figurinhaMap}
                                figurinhaItems={figurinhaItems}
                                forceInlineSubthread
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    if (typeof document === 'undefined') return null;
    return createPortal(content, document.body);
}

function CommentItem({
    comment,
    chapterId,
    obraId,
    onRefresh,
    onImageClick,
    onXpGain,
    isReply = false,
    depth = 0,
    highlightedId,
    comunidade = false,
    feedContexto = false,
    feedSomenteLeitura = false,
    figurinhaMap,
    figurinhaItems,
    forceInlineSubthread = false,
}: CommentItemProps) {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const isHighlighted = highlightedId === comment.id;
    const { isSpoiler, displayText } = parseSpoiler(comment.comentario);
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [replyMarkSpoiler, setReplyMarkSpoiler] = useState(false);
    const [submittingReply, setSubmittingReply] = useState(false);
    const [liking, setLiking] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deepRepliesModalOpen, setDeepRepliesModalOpen] = useState(false);
    const replyTextareaRef = useRef<HTMLTextAreaElement>(null);

    const viewerId = resolveSessionUserId(user);
    const canDelete =
        isAuthenticated && viewerId !== null && viewerId === resolveCommentAuthorId(comment.usuario);
    const canReply = isAuthenticated && depth < 3 && !feedSomenteLeitura;

    const scAmount = Number(comment.superchat?.amount ?? 0);
    const superchatCardTone =
        comment.superchat?.ativo && Number.isFinite(scAmount) && scAmount > 0
            ? scAmount < 10
                ? 'border border-emerald-400/20 bg-emerald-500/[0.06]'
                : scAmount < 20
                  ? 'border border-emerald-400/24 bg-emerald-500/[0.08]'
                  : scAmount < 50
                    ? 'border border-emerald-400/28 bg-emerald-500/[0.10]'
                    : scAmount < 100
                      ? 'border border-emerald-400/32 bg-emerald-500/[0.12]'
                      : 'border border-emerald-400/38 bg-emerald-500/[0.14]'
            : '';

    const handleLike = async () => {
        if (!isAuthenticated) {
            alert('Você precisa estar logado para curtir comentários');
            return;
        }

        setLiking(true);
        try {
            const response: any = await api.post(`comentarios/${comment.id}/curtir`, {}, { requiresAuth: true });
            if (response.xp > 0 && onXpGain) onXpGain(response.xp, 'Curtida');
            onRefresh();
        } catch (error) {
            console.error('Erro ao curtir comentário:', error);
        } finally {
            setLiking(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm('Tem certeza que deseja excluir este comentário?')) {
            return;
        }

        setDeleting(true);
        try {
            await api.delete(`comentarios/${comment.id}`, { requiresAuth: true });
            onRefresh();
        } catch (error) {
            console.error('Erro ao excluir comentário:', error);
            alert('Erro ao excluir comentário. Tente novamente.');
        } finally {
            setDeleting(false);
        }
    };

    const insertFigurinhaInReply = (code: string) => {
        const token = `:${code}:`;
        const ta = replyTextareaRef.current;
        const prev = replyContent;
        if (!ta) {
            setReplyContent(prev.trim() ? `${prev.trim()} ${token}` : token);
            return;
        }
        const start = ta.selectionStart ?? prev.length;
        const end = ta.selectionEnd ?? prev.length;
        const before = prev.slice(0, start);
        const after = prev.slice(end);
        const needsSpace = before.length > 0 && !/\s$/.test(before);
        const spacer = needsSpace ? ' ' : '';
        const gap = after.length > 0 && !after.startsWith(' ') ? ' ' : '';
        const next = before + spacer + token + gap + after;
        const caret = (before + spacer + token + gap).length;
        setReplyContent(next);
        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(caret, caret);
        });
    };

    const handleSubmitReply = async () => {
        if (!replyContent.trim()) return;

        setSubmittingReply(true);
        try {
            const replyText = replyContent.trim();
            const payload: any = {
                comentario: replyMarkSpoiler ? `[SPOILER] ${replyText}` : replyText,
                parente: comment.id.toString()
            };

            if (chapterId) {
                payload.capitulo = chapterId.toString();
            } else if (obraId) {
                payload.obra = obraId.toString();
            }

            const response: any = await api.post('comentarios', payload, { requiresAuth: true });

            if (response.xp > 0 && onXpGain) onXpGain(response.xp, 'Resposta');

            setReplyContent('');
            setReplyMarkSpoiler(false);
            setShowReplyForm(false);
            onRefresh();
        } catch (error) {
            console.error('Erro ao enviar resposta:', error);
            alert('Erro ao enviar resposta. Tente novamente.');
        } finally {
            setSubmittingReply(false);
        }
    };

    const getCommentImageUrl = (comment: Comment): string => {
        if (!comment.imagem) return '';
        if (comment.imagem.startsWith('http://') || comment.imagem.startsWith('https://') || comment.imagem.startsWith('data:')) {
            return comment.imagem;
        }
        const cleanImage = comment.imagem.replace(/^\/+/, '');
        return `${imageUrl}comentarios/${cleanImage}`;
    };

    const avatarClip = avatarClipClassFromMolduraTipo(comment.usuario.mol_tipo);

    const renderChildrenInline =
        Boolean(comment.filhos?.length) &&
        (forceInlineSubthread || depth <= MAX_PARENT_DEPTH_FOR_INLINE_REPLIES);
    const showDeepRepliesButton =
        !forceInlineSubthread &&
        depth === MAX_PARENT_DEPTH_FOR_INLINE_REPLIES + 1 &&
        Boolean(comment.filhos?.length);
    const deepReplyCount = showDeepRepliesButton ? countCommentsInSubtree(comment.filhos) : 0;

    const threadIndentDepth = isReply ? Math.min(depth, MAX_COMMENT_THREAD_INDENT_DEPTH) : 0;
    const threadIndentStyle =
        threadIndentDepth > 0
            ? ({ marginInlineStart: `${threadIndentDepth * 14}px` } as React.CSSProperties)
            : undefined;

    return (
        <div
            id={`comment-${comment.id}`}
            style={threadIndentStyle}
            className={`
                ${isReply ? 'pl-2 md:pl-3 border-l-2 border-white/10' : ''}
                overflow-hidden transition-all duration-500 rounded-xl p-3 md:p-3.5
                ${isHighlighted ? 'bg-orange-500/20 ring-2 ring-orange-500/50 animate-pulse' : ''}
                ${superchatCardTone}
            `}
        >
            <div className="flex gap-2 md:gap-4">
                {/* Avatar */}
                <Link to={`/perfil/${comment.usuario.nick}`} className="flex-shrink-0">
                    <div className="relative">
                        {comment.usuario.moldura && (
                            <div className="absolute -inset-1 z-20 pointer-events-none">
                                <img
                                    src={getMolduraImageUrl(comment.usuario.moldura) || ''}
                                    className="w-[100%] h-[100%] max-w-none max-h-none -left-[20%] -top-[20%]"
                                    alt=""
                                />
                            </div>
                        )}
                        <div
                            className={`w-12 h-12 md:w-14 md:h-14 overflow-hidden border border-white/10 flex items-center justify-center bg-neutral-800 relative z-10 ${avatarClip}`}
                        >
                            <img
                                src={getUserImageUrl(comment.usuario.id, comment.usuario.imagem)}
                                alt={comment.usuario.nome}
                                className={`w-full h-full object-cover ${avatarClip}`}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                                }}
                            />
                        </div>
                        <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 z-30 min-w-max">
                            <LevelBadge level={comment.usuario.nivel || 1} size="sm" className="shadow-black/50" />
                        </div>
                    </div>
                </Link>

                {/* Conteúdo */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 md:gap-4 mb-2">
                        <div className="flex-1 min-w-0">
                            <Link to={`/perfil/${comment.usuario.nick}`} className="hover:text-orange-500 transition-colors flex items-center gap-2">
                                <h4 className="text-white font-semibold text-xs md:text-sm truncate">{comment.usuario.nome}</h4>
                            </Link>
                            {comment.usuario.tags && comment.usuario.tags.length > 0 && (
                                <div className="flex gap-1.5 md:gap-2 mt-1 flex-wrap">
                                    {comment.usuario.tags.map((tag) => (
                                        <UserTagBadge key={tag.tag_id} tag={tag as any} size="sm" />
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                            {comment.superchat?.ativo && Number.isFinite(scAmount) && scAmount > 0 ? (
                                <span className="inline-flex items-center gap-1.5 text-[10px] md:text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/16 text-emerald-200 border border-emerald-400/24 font-semibold whitespace-nowrap">
                                    <CircleDollarSign size={12} className="shrink-0" />
                                    Super Chat R$ {scAmount.toFixed(2)}
                                </span>
                            ) : null}
                            <span className="text-white/40 text-[10px] md:text-xs whitespace-nowrap">
                                {dayjs(comment.criado_em).fromNow()}
                            </span>
                        </div>
                    </div>

                    {isSpoiler ? (
                        <SpoilerOverlay className="mb-3">
                            <p className="text-white/80 text-xs md:text-sm mb-3 leading-relaxed break-words">
                                {renderCommentWithFigurinhas(displayText, figurinhaMap)}
                            </p>
                            {comment.imagem && (
                                <div className="mb-3">
                                    <img
                                        src={getCommentImageUrl(comment) || ''}
                                        alt="Imagem do comentário"
                                        className="max-w-full md:max-w-[300px] max-h-[150px] md:max-h-[200px] rounded-lg border border-white/10 cursor-pointer hover:border-orange-500/50 transition-colors"
                                        onClick={(e) => { e.stopPropagation(); onImageClick(getCommentImageUrl(comment) || ''); }}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}
                        </SpoilerOverlay>
                    ) : (
                        <>
                            <p className="text-white/80 text-xs md:text-sm mb-3 leading-relaxed break-words">
                                {renderCommentWithFigurinhas(comment.comentario, figurinhaMap)}
                            </p>
                            {comment.imagem && (
                                <div className="mb-3">
                                    <img
                                        src={getCommentImageUrl(comment) || ''}
                                        alt="Imagem do comentário"
                                        className="max-w-full md:max-w-[300px] max-h-[150px] md:max-h-[200px] rounded-lg border border-white/10 cursor-pointer hover:border-orange-500/50 transition-colors"
                                        onClick={() => onImageClick(getCommentImageUrl(comment) || '')}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {feedContexto && !isReply && comment.contexto?.tipo === 'lista' && comment.contexto.lista_id ? (
                        <Link
                            to={`/listas/${comment.contexto.lista_id}`}
                            className="mt-3 flex gap-3 p-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] hover:border-orange-500/30 rounded-xl transition-all group/work mb-3"
                        >
                            <div className="min-w-0 flex flex-col justify-center">
                                <span className="mb-1 w-fit text-[8px] font-black uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-violet-600/20 text-violet-300 border border-violet-500/20">
                                    Lista
                                </span>
                                <h4 className="font-bold text-white text-xs md:text-sm leading-tight line-clamp-2 group-hover/work:text-orange-400 transition-colors">
                                    {comment.contexto.lista_nome || 'Lista'}
                                </h4>
                            </div>
                        </Link>
                    ) : null}

                    {feedContexto && !isReply && comment.contexto?.tipo === 'comunidade' ? (
                        <Link
                            to="/comunidade"
                            className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white/70 hover:text-white hover:border-orange-500/30 text-xs mb-3"
                        >
                            Comunidade
                        </Link>
                    ) : null}

                    {feedContexto &&
                    !isReply &&
                    comment.contexto &&
                    (comment.contexto.tipo === 'obra'
                        ? comment.contexto.obra_id
                        : comment.contexto.tipo === 'capitulo' &&
                          comment.contexto.capitulo_id &&
                          capituloContextoTemDadosAlemDoId(comment.contexto)) ? (
                        <div className="mt-3 mb-3 w-full">
                            <MangaReferenceCard
                                contextKind={comment.contexto.tipo === 'obra' ? 'obra' : 'capitulo'}
                                title={
                                    comment.contexto.obra_nome?.trim() ||
                                    comment.contexto.capitulo_nome?.trim() ||
                                    (comment.contexto.capitulo_id
                                        ? `Capítulo ${comment.contexto.capitulo_id}`
                                        : 'Capítulo')
                                }
                                chapter={
                                    comment.contexto.capitulo_numero ??
                                    comment.contexto.capitulo_nome ??
                                    null
                                }
                                cover={
                                    comment.contexto.obra_id
                                        ? getObraImageUrl(
                                              comment.contexto.obra_id,
                                              comment.contexto.obra_imagem || undefined,
                                              200,
                                          ) || ''
                                        : ''
                                }
                                onClick={() => {
                                    const ctx = comment.contexto!;
                                    if (ctx.tipo === 'obra' && ctx.obra_id) {
                                        navigate(generateWorkLink(ctx.obra_id));
                                        return;
                                    }
                                    if (ctx.tipo === 'capitulo' && ctx.capitulo_id) {
                                        navigate(generateChapterLink(ctx.capitulo_id));
                                        return;
                                    }
                                    if (ctx.obra_id) {
                                        navigate(generateWorkLink(ctx.obra_id));
                                    }
                                }}
                            />
                        </div>
                    ) : null}

                    {/* Ações */}
                    <div className="flex items-center flex-wrap gap-3">
                        <button
                            onClick={handleLike}
                            disabled={liking}
                            className={`flex items-center gap-1.5 text-xs transition-colors whitespace-nowrap ${comment.curtido
                                ? 'text-red-500'
                                : 'text-white/40 hover:text-red-500'
                                }`}
                        >
                            <Heart size={14} className={comment.curtido ? 'fill-current' : ''} />
                            <span>{comment.total_curtidas || 0}</span>
                        </button>

                        {canReply && (
                            <button
                                onClick={() => setShowReplyForm(!showReplyForm)}
                                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors whitespace-nowrap"
                            >
                                <MessageSquare size={14} />
                                <span className="hidden sm:inline">Responder</span>
                            </button>
                        )}

                        {canDelete && (
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-500 transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                                <Trash2 size={14} />
                                <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
                            </button>
                        )}

                        {showDeepRepliesButton && (
                            <button
                                type="button"
                                onClick={() => setDeepRepliesModalOpen(true)}
                                className="flex items-center gap-1.5 text-xs text-orange-400/90 hover:text-orange-300 transition-colors whitespace-nowrap"
                            >
                                <MessageSquare size={14} />
                                <span>
                                    Ver respostas{deepReplyCount > 0 ? ` (${deepReplyCount})` : ''}
                                </span>
                            </button>
                        )}
                    </div>

                  

                    {/* Formulário de resposta */}
                    {showReplyForm && (
                        <div className="mt-4 space-y-3">
                            <div className="flex gap-2 items-start">
                                <textarea
                                    ref={replyTextareaRef}
                                    value={replyContent}
                                    onChange={(e) => setReplyContent(e.target.value)}
                                    placeholder="Escreva sua resposta..."
                                    className="flex-1 min-w-0 w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-white/40 focus:outline-none focus:border-orange-500/50 resize-none text-sm"
                                    rows={3}
                                    maxLength={1000}
                                />
                                <div className="shrink-0 pt-1">
                                    <FigurinhaPicker
                                        items={figurinhaItems}
                                        onPick={insertFigurinhaInReply}
                                        disabled={submittingReply}
                                        placement="below"
                                    />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-white/60 hover:text-white/80 cursor-pointer text-xs">
                                <input
                                    type="checkbox"
                                    checked={replyMarkSpoiler}
                                    onChange={(e) => setReplyMarkSpoiler(e.target.checked)}
                                    className="rounded border-white/30 bg-white/5 text-orange-500 focus:ring-orange-500/50"
                                />
                                <EyeOff size={12} />
                                Marcar como spoiler
                            </label>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <span className="text-white/40 text-xs">{replyContent.length}/1000</span>
                                <div className="flex gap-2 w-full sm:w-auto">
                                    <button
                                        onClick={() => {
                                            setShowReplyForm(false);
                                            setReplyContent('');
                                        }}
                                        className="flex-1 sm:flex-initial px-4 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white/70 hover:text-white transition-colors text-xs whitespace-nowrap"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleSubmitReply}
                                        disabled={!replyContent.trim() || submittingReply}
                                        className="flex-1 sm:flex-initial px-4 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                    >
                                        {submittingReply ? 'Enviando...' : 'Responder'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Respostas só sob a raiz na página; thread mais profunda no modal */}
                    {renderChildrenInline && comment.filhos && (
                        <div className="mt-5 space-y-5 md:space-y-6">
                            {comment.filhos.map((reply) => (
                                <CommentItem
                                    key={reply.id}
                                    comment={reply}
                                    chapterId={chapterId}
                                    obraId={obraId}
                                    onRefresh={onRefresh}
                                    onImageClick={onImageClick}
                                    isReply={true}
                                    depth={depth + 1}
                                    onXpGain={onXpGain}
                                    highlightedId={highlightedId}
                                    comunidade={comunidade}
                                    feedContexto={feedContexto}
                                    feedSomenteLeitura={feedSomenteLeitura}
                                    figurinhaMap={figurinhaMap}
                                    figurinhaItems={figurinhaItems}
                                    forceInlineSubthread={forceInlineSubthread}
                                />
                            ))}
                        </div>
                    )}

                    {deepRepliesModalOpen && comment.filhos && comment.filhos.length > 0 ? (
                        <DeepRepliesThreadModal
                            replies={comment.filhos}
                            parentDepth={depth}
                            onClose={() => setDeepRepliesModalOpen(false)}
                            chapterId={chapterId}
                            obraId={obraId}
                            onRefresh={onRefresh}
                            onImageClick={onImageClick}
                            onXpGain={onXpGain}
                            highlightedId={highlightedId}
                            comunidade={comunidade}
                            feedContexto={feedContexto}
                            feedSomenteLeitura={feedSomenteLeitura}
                            figurinhaMap={figurinhaMap}
                            figurinhaItems={figurinhaItems}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}
